import React, { type ReactNode, useImperativeHandle, useMemo, useRef } from 'react';

import {
	getCoreRowModel,
	getExpandedRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useInfiniteScroll } from '../../shared-hooks/use-infinite-scroll';
import { useKeyboardNavigation } from '../../shared-hooks/use-keyboard-navigation';

import { type CharlieTableProps, TableType } from './charlie-table.types';
import {
	CharlieTableBody,
	type CharlieTableBodyProps,
	MemoCharlieTableBody,
} from './components/body/charlie-table-body';
import { CharlieTableHeadRow } from './components/head/charlie-table-head-row';
import { THead } from './components/head/thead';
import { LoadMore } from './components/loading/load-more';
import { Loading } from './components/loading/loading';
import { Table } from './components/table';
import { defaultLabels } from './constants/charlie-table.default-labels';
import { useColumnCssVars } from './hooks/use-column-css-vars';
import { useCharlieColumnVirtualization } from './hooks/virtualization/use-charlie-column-virtualization';
import { fetchMoreOnScroll } from './utils/fetch-more-on-scroll';
import { manualGroupedRowModel } from './utils/manual-group-model';
import {
	createReservedColumn,
	processColumnStates,
	processReservedColumns,
	RESERVED_COLUMN_IDS,
	type ReservedColumnConfig,
} from './utils/reserved-column.utils';
import { CharlieTableDndWrapper } from './wrappers/charlie-table-dnd-wrapper';
import { CharlieTableMinimalDndWrapper } from './wrappers/charlie-table-minimal-dnd-wrapper';

const styles = cssMap({
	LoadingTable: { opacity: token('opacity.loading'), pointerEvents: 'none' },

	TableWrapper: {
		maxHeight: '100vh',
		position: 'relative', //needed for sticky header
		isolation: 'isolate',
		display: 'flex',
		zIndex: 0,
		flex: 1,
		flexDirection: 'column',
		height: '100%',
		overflow: 'auto',
		borderTopLeftRadius: token('radius.small'),
		borderTopRightRadius: token('radius.small'),
	},

	TableWrapperT26: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},

	Table: {
		borderCollapse: 'separate',
		borderSpacing: '0px',
		width: '100%',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},
	FixedTableLayout: {
		tableLayout: 'fixed',
	},
	AutoTableLayout: {
		tableLayout: 'auto',
	},

	LoadingOverlayStyle: {
		position: 'absolute',
		justifyContent: 'center',
		alignItems: 'center',
		pointerEvents: 'none',
		top: token('space.0'),
		left: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
	},

	OuterBorder: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
		overflow: 'hidden',
		position: 'relative',
	},
});

/**
 * Type-safe enum for Table State
 */
export const TableState = {
	loaded: 'LOADED',
	loading: 'LOADING',
	error: 'ERROR',
} as const;
export type TableState = (typeof TableState)[keyof typeof TableState];

const Caption = ({
	tableCaption,
}: {
	tableCaption: CharlieTableProps<any>['tableCaption'];
}): ReactNode | null => {
	if (!tableCaption) {
		return null;
	}
	if (typeof tableCaption === 'function') {
		return tableCaption();
	}
	return <caption>{tableCaption}</caption>;
};

/**
 * CharlieTable is the default table component that
 * should be used for full page table experiences. This is not meant
 * necessarily for one-off table layout content.
 *
 * Powered by Tanstack and a forked version @atlaskit/table
 * for the primitives, this table should be able to handle most and or all
 * use cases within the core experience.
 */
export const CharlieTable = <T extends object & { overrideRender?: (rowData: T) => ReactNode }>({
	// Any tanstack params set as a default here needs to be added to the hook below
	labels = defaultLabels,
	manualSorting = true,
	manualPagination = true,
	enableRowReorder = false,
	enableResizing = false,
	columnResizeMode = 'onChange',
	tableType = TableType.input,
	tableState = TableState.loaded,
	rowSpacing = 'default',
	rowCellXcss,
	headerCellXcss,
	tableWrapperXcss,
	enableSortingRemoval = false,
	enableKeyboardNavigation = false,
	enableRowVirtualization: enableRowVirtualizationProp = false,
	enableColumnVirtualization: enableColumnVirtualizationProp = false,
	tableCaption,
	responsiveColumnSizing = undefined,
	...props
}: CharlieTableProps<T>): React.JSX.Element => {
	// Can't be changed after the table is rendered
	const enableRowVirtualization = useMemo(
		() => enableRowVirtualizationProp,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const enableColumnVirtualization = useMemo(
		() => enableColumnVirtualizationProp,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Ensure that the labels are always defined
	const allLabels = { ...defaultLabels, ...labels };
	const tableRef = useRef<HTMLTableElement>(null);
	useImperativeHandle(props.refs?.tableRef, () => tableRef.current!);

	const containerRef = useRef<HTMLDivElement>(null);
	useImperativeHandle(props.refs?.tableContainerRef, () => containerRef.current!);

	const tbodyRef = useRef<HTMLTableSectionElement>(null);
	useImperativeHandle(props.refs?.tableBodyRef, () => tbodyRef.current!);

	const { tableNavigationControllers } = useKeyboardNavigation();

	const infiniteScrollRef = useInfiniteScroll<HTMLTableRowElement>({
		onScrollTo: () => {
			props.onLoadNext?.();
		},
	});

	const lastSelectedRowIdRef = useRef<string | null>(null);

	const CharlieWrapper = useMemo(() => {
		if (props.enableColumnReorder || enableRowReorder || enableResizing) {
			return CharlieTableDndWrapper;
		}
		return CharlieTableMinimalDndWrapper;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.enableColumnReorder, enableRowReorder, enableResizing]);

	const reservedColumnConfigs = useMemo(() => {
		const configs: ReservedColumnConfig<T>[] = [];

		if (props.enableRowSelection) {
			const checkboxConfig = createReservedColumn<T>({
				id: RESERVED_COLUMN_IDS.CHECKBOX,
				feature: 'checkbox',
				position: 'start',
				labels: {
					selectRow: allLabels.selectRow,
					selectAllRows: allLabels.selectAllRows,
				},
				analyticsContext: props.analyticsContext,
				lastSelectedRowIdRef,
			});
			configs.push(checkboxConfig);
		}

		return configs;
	}, [
		props.enableRowSelection,
		allLabels.selectRow,
		allLabels.selectAllRows,
		props.analyticsContext,
	]);

	const reservedColumnResult = useMemo(
		() =>
			processReservedColumns({
				reservedColumns: reservedColumnConfigs,
				originalColumns: props.columnDefs,
			}),
		[reservedColumnConfigs, props.columnDefs],
	);

	const tanModifiedColumns = useMemo(() => {
		if (!props.columnDefs) {
			return undefined;
		}
		return reservedColumnResult.modifiedColumns;
	}, [props.columnDefs, reservedColumnResult.modifiedColumns]);

	const processedColumnStates = useMemo(
		() =>
			processColumnStates(
				reservedColumnResult,
				{
					columnOrder: props.state?.columnOrder,
					columnPinningLeft: props.state?.columnPinning?.left,
				},
				{
					columnOrder: props.initialState?.columnOrder,
					columnPinningLeft: props.initialState?.columnPinning?.left,
				},
			),
		[
			reservedColumnResult,
			props.state?.columnOrder,
			props.state?.columnPinning?.left,
			props.initialState?.columnOrder,
			props.initialState?.columnPinning?.left,
		],
	);

	const {
		modifiedColumnOrder,
		modifiedInitialColumnOrder,
		modifiedColumnPinningLeft,
		modifiedInitialColumnPinningLeft,
	} = processedColumnStates;

	const modifiedColumnPinning = useMemo(
		() =>
			modifiedColumnPinningLeft
				? {
						...props.state?.columnPinning,
						left: modifiedColumnPinningLeft,
					}
				: props.state?.columnPinning,
		[modifiedColumnPinningLeft, props.state?.columnPinning],
	);

	const modifiedInitialColumnPinning = useMemo(
		() =>
			modifiedInitialColumnPinningLeft
				? { ...props.initialState?.columnPinning, left: modifiedInitialColumnPinningLeft }
				: props.initialState?.columnPinning,
		[modifiedInitialColumnPinningLeft, props.initialState?.columnPinning],
	);

	const stableEmptyTanArray: Array<T> = useMemo(() => [], []);
	const columns = useMemo(() => tanModifiedColumns ?? [], [tanModifiedColumns]);
	const memoedData = useMemo(
		() => props.data ?? stableEmptyTanArray,
		[props.data, stableEmptyTanArray],
	);

	// extract external callback to manually call based on columnResizeMode
	const onColumnSizingChangeRef = useRef(props.onColumnSizingChange);
	onColumnSizingChangeRef.current = props.onColumnSizingChange;

	const { onColumnSizingChange: _, ...propsWithoutSizingCallback } = props;

	const table = useReactTable<T>({
		...propsWithoutSizingCallback,
		defaultColumn: {
			...props.defaultColumn,
			// setting a min size of 26 here to avoid breaking styling when columns are too small
			minSize: Math.max(26, props.defaultColumn?.minSize ?? 0),
		},
		columns,
		data: memoedData,
		autoResetExpanded: props.autoResetExpanded,
		state: {
			...props.state,
			...(modifiedColumnPinning && { columnPinning: modifiedColumnPinning }),
			...(modifiedColumnOrder && { columnOrder: modifiedColumnOrder }),
		},
		initialState: {
			...props.initialState,
			...(modifiedInitialColumnPinning && { columnPinning: modifiedInitialColumnPinning }),
			...(modifiedInitialColumnOrder && { columnOrder: modifiedInitialColumnOrder }),
		},
		enableSortingRemoval: enableSortingRemoval,
		enableRowSelection: props.enableRowSelection,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getGroupedRowModel: props.enableServerSideGrouping
			? manualGroupedRowModel()
			: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		manualSorting,
		manualPagination,
	});

	useImperativeHandle(props.refs?.internalTableRef, () => table, [table]);

	const { columnSizing, columnSizingInfo } = table.getState();

	const hasWidths = Object.keys(columnSizing ?? {}).length > 0 || enableResizing;

	const { columnCssVars, columnSizeVars, columnResizeVars, handleResizeHandleHoverChange } =
		useColumnCssVars({
			table,
			columnSizing,
			columnSizingInfo,
			tableType,
			tableRef,
		});

	const isEmptyState = table.options.data.length === 0 && tableState === TableState.loaded;

	const columnVirtualizer = useCharlieColumnVirtualization({
		table,
		enableColumnVirtualization,
		enableColumnPinning: table.getIsSomeColumnsPinned(),
		tableContainerRef: containerRef,
		columnVirtualizerOptions: props.columnVirtualizerOptions,
	});

	const commonTableBodyProps: CharlieTableBodyProps<T> = {
		table,
		rowSpacing,
		tableType,
		rowCellXcss,
		labels: allLabels,
		renderOptions: props.renderOptions,
		enableRowSelection: props.enableRowSelection,
		onRowClick: props.onRowClick,
		interactionName: props.interactionName,
		renderChildComponent: props.renderChildComponent,
		analyticsContext: props.analyticsContext,
		tableBodyRef: tbodyRef,
		hasNext: props.hasNext,
		enableRowVirtualization,
		enableKeyboardNavigation,
		tableNavigationControllers,
		containerRef,
		rowVirtualizerRef: props.refs?.rowVirtualizerRef,
		rowVirtualizerOptions: props.rowVirtualizerOptions,
		infiniteScrollRef,
		responsiveColumnSizing,
		columnVirtualizer,
		enableRowReorder: enableRowReorder,
		onDraggingRowEnd: props.onDraggingRowEnd,
		componentPropOverrides: props.componentPropOverrides,
	};

	const isColumnSizing = columnSizingInfo.isResizingColumn !== false;

	const shapeThemeRefresh = fg('charlie-table-shape-refresh');

	const scrollableContent = (
		//normally you shouldn't do this, but was having trouble passing the generic with the lazy loaded component
		//@ts-expect-error lazy loading issues
		<CharlieWrapper
			xcss={cx(
				styles.TableWrapper,
				tableWrapperXcss,
				fg('platform-dst-shape-theme-default') && styles.TableWrapperT26,
			)}
			ref={containerRef}
			{...(props.hasNext &&
				props.onLoadNext != null && {
					onScroll: (event: React.UIEvent<HTMLDivElement>) =>
						fetchMoreOnScroll({
							containerRefElement: event.target as HTMLDivElement,
							onScrollTo: props.onLoadNext!,
							tableState,
						}),
				})}
			{...(props.enableColumnReorder || enableRowReorder || enableResizing
				? {
						charlieTableState: table,
						tableRef: tableRef,
						tbodyRef: tbodyRef,
						enableRowReorder: enableRowReorder,
						enableColumnReorder: props.enableColumnReorder,
						onDraggingRowEnd: props.onDraggingRowEnd,
					}
				: {
						charlieTableState: table,
						tableRef: tableRef,
						tbodyRef: tbodyRef,
					})}
		>
			<Table
				aria-label={labels?.table}
				testId={props.testId}
				ref={tableRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={{ ...columnCssVars, ...columnSizeVars, ...columnResizeVars }}
				xcss={cx(
					tableState === TableState.loading ? styles.LoadingTable : false,
					styles.Table,
					hasWidths && !enableRowVirtualization ? styles.FixedTableLayout : styles.AutoTableLayout,
				)}
			>
				<Caption tableCaption={tableCaption} />
				<THead>
					{table.getHeaderGroups().map((headerGroup) => (
						<CharlieTableHeadRow<T>
							key={headerGroup.id}
							headerGroup={headerGroup}
							table={table}
							enableRowVirtualization={enableRowVirtualization}
							labels={allLabels}
							enableResizing={enableResizing}
							columnResizeMode={columnResizeMode}
							tableType={tableType}
							enableColumnReorder={props.enableColumnReorder}
							hasWidths={hasWidths}
							headerCellXcss={headerCellXcss}
							enableColumnActions={props.enableColumnActions}
							responsiveColumnSizing={responsiveColumnSizing}
							columnVirtualizer={columnVirtualizer}
							columnActions={props.columnActions}
							onColumnSizingChangeRef={onColumnSizingChangeRef.current}
							onResizeHandleHoverChange={handleResizeHandleHoverChange}
						/>
					))}
				</THead>
				{(!enableRowVirtualization && !enableColumnVirtualization) || isColumnSizing ? (
					<MemoCharlieTableBody {...commonTableBodyProps} />
				) : (
					<CharlieTableBody {...commonTableBodyProps} />
				)}
			</Table>
			<LoadMore
				hasNext={props.hasNext}
				onLoadNext={props.onLoadNext}
				isLoading={tableState === TableState.loading}
				isEmptyState={isEmptyState}
				isError={tableState === TableState.error}
				hidePaginationEndIndicator={props.hidePaginationEndIndicator}
				loadMoreLabel={allLabels.loadMoreLabel ?? 'Load more rows'}
				loadMore={allLabels.loadMore ?? 'Load more'}
			/>
			{tableState === TableState.loading && !shapeThemeRefresh && (
				<Flex xcss={styles.LoadingOverlayStyle}>
					<Spinner size="large" testId="common-table-spinner" />
				</Flex>
			)}
			{tableState === TableState.error && props.errorView}
			{isEmptyState && props.emptyTableView}
		</CharlieWrapper>
	);

	return shapeThemeRefresh ? (
		<Box xcss={styles.OuterBorder}>
			{scrollableContent}
			{tableState === TableState.loading && <Loading label={allLabels.loadingMore} />}
		</Box>
	) : (
		scrollableContent
	);
};
